/**
 * OptionsService is a class responsible for managing and loading options from a file.
 * It uses HTTP requests to fetch the options and provides methods to access them.
 */
class OptionsService {
    /**
     * Constructs an instance of OptionsService.
     * @param {Object} $http - The HTTP service for making requests.
     * @param {Object} $bridges - The bridges service for accessing various utilities.
     * @param {Object} $config - The configuration service for getting URLs and other settings.
     */
    constructor($http, $bridges, $config) {
        this.$http = $http;
        this.$bridges = $bridges;
        this.$config = $config;

        this._logIdentifier = 'OptionsService';
        this._options = null;
        this.$logger = this.$bridges.logger();
        this.$logger.info(this, 'initialize');
    }

    /**
     * Loads the options file asynchronously and calls the provided callback with the loaded data.
     * @param {Function} onDataLoaded - A callback function to be called with the loaded options data.
     */
    loadOptionsFile(onDataLoaded) {
        this.$logger.logEnter(this, 'loadOptionsFile');

        try {
            const successCB = (response) => {
                this._options = response.data;
                this.$logger.info(this, "Options file loaded successfully, calling handler. " + JSON.stringify(this._options));
                onDataLoaded(this._options);
            };
            const errorCB = (response) => {
                this.$logger.error(this, "Options file loading failed, status (" + response.status + ") data (" + response.data + ")");
            };
            this.$http.get(this.$config.getOptionsFileURL()).then(successCB, errorCB);
            this.$logger.info(this, 'Loading options file triggered');
        } catch (e) {
            this.$logger.error(this, 'Could not trigger loading of options file: ' + e);
        }
    }

    /**
     * Retrieves a specific option from the loaded options.
     * @param {string} optionName - The name of the option to retrieve.
     * @returns {any} The value of the option or null if the option does not exist or an error occurs.
     */
    getOption(optionName) {
        try {
            if (this._options && this._options.hasOwnProperty(optionName)) {
                return this._options[optionName];
            } else {
                this.$logger.warn(this, `Option "${optionName}" not found.`);
                //console.error("getOption", optionName, this._options);
                return null;
            }
        } catch (e) {
            this.$logger.error(this, `Error retrieving option "${optionName}": ` + e);
            return null;
        }
    }
}

export default OptionsService;